import React, { memo } from 'react';
import { Edit2, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';

// Task summary type for list view
export interface TaskSummary {
  id: number;
  name: string;
  description?: string | null;
  isActive?: boolean;
  taskDefinitionType?: 'POSTING' | 'REMOVAL';
  parentTaskId?: number | null;
}

export interface TaskRowProps {
  task: TaskSummary;
  isEditing: boolean;
  showArchived: boolean;
  onEdit: (task: TaskSummary) => void;
  onArchive: (id: number) => void;
  onUnarchive: (id: number) => void;
  onDelete: (id: number) => void;
  isArchiving: boolean;
  isUnarchiving: boolean;
  isDeleting: boolean;
  editForm: React.ReactNode;
}

const TaskRow = memo(function TaskRow({
  task,
  isEditing,
  showArchived,
  onEdit,
  onArchive,
  onUnarchive,
  onDelete,
  isArchiving,
  isUnarchiving,
  isDeleting,
  editForm,
}: TaskRowProps) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            {isEditing ? (
              editForm
            ) : (
              <>
                <CardTitle className="flex items-center gap-2">
                  {task.name}
                  {task.taskDefinitionType === 'REMOVAL' ? (
                    <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-300">
                      Removal
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-300">
                      Posting
                    </Badge>
                  )}
                  {!task.isActive && (
                    <Badge variant="destructive">
                      Inactive
                    </Badge>
                  )}
                </CardTitle>
                {task.description && (
                  <CardDescription>{task.description}</CardDescription>
                )}
              </>
            )}
          </div>
          {!isEditing && (
            <div className="flex items-center space-x-2">
              {!showArchived ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onEdit(task)}
                    title="Edit task"
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onArchive(task.id)}
                    disabled={isArchiving}
                    title="Archive task (hide but keep historical data)"
                  >
                    Archive
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onUnarchive(task.id)}
                    disabled={isUnarchiving}
                    title="Restore task"
                  >
                    Restore
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => onDelete(task.id)}
                    disabled={isDeleting}
                    title="⚠️ Permanently delete (breaks historical data!)"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </CardHeader>
    </Card>
  );
});

TaskRow.displayName = 'TaskRow';

export default TaskRow;










